import { SidebarTooltip } from "./Tooltip";
import { useSidebar } from "./SidebarContext";
import { Link } from "@tanstack/react-router";
import type { NavItem } from "./types";

interface SidebarNavItemProps extends NavItem {
  indent?: boolean;
}

export function SidebarNavItem({
  icon: Icon,
  label,
  badge,
  indent,
  to,
}: SidebarNavItemProps) {
  const { collapsed } = useSidebar();

  const content = (
    <>
      <Icon
        size={17}
        strokeWidth={1.75}
        className="shrink-0 transition-colors duration-150"
      />
      {!collapsed && (
        <>
          <span className="flex-1 text-left text-[13px] tracking-[-0.01em]">
            {label}
          </span>
          {badge != null && (
            <span className="min-w-[18px] rounded-[5px] bg-card-hover/60 px-1.5 py-[1px] text-center text-[10.5px] font-semibold tabular-nums text-muted">
              {badge}
            </span>
          )}
        </>
      )}
    </>
  );

  const baseClassName = `group/item relative flex w-full items-center rounded-md text-[13px] transition-all duration-150 ${
    collapsed
      ? "justify-center px-0 mx-auto w-10 h-8"
      : `gap-2.5 py-[5px] ${indent ? "pl-3 pr-2" : "pl-3.5 pr-2"}`
  }`;

  const activeClassName = `${baseClassName} bg-accent-soft text-primary font-medium before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-4 before:w-[3px] before:rounded-full before:bg-accent before:shadow-[0_0_6px_var(--color-accent-medium)]`;

  const inactiveClassName = `${baseClassName} text-secondary hover:bg-card-hover/40 hover:text-primary`;

  if (to) {
    return (
      <SidebarTooltip label={label} enabled={collapsed}>
        <Link
          to={to}
          params={(prev) => prev}
          search={(prev) => prev}
          className={baseClassName}
          activeProps={{ className: activeClassName }}
          inactiveProps={{ className: inactiveClassName }}
        >
          {content}
        </Link>
      </SidebarTooltip>
    );
  }

  return (
    <SidebarTooltip label={label} enabled={collapsed}>
      <button type="button" className={inactiveClassName}>
        {content}
      </button>
    </SidebarTooltip>
  );
}
